import { useCallback } from 'react';

import { useProjectSiteParams } from '@/shared/lib';
import { useLazyExportServiceRequestsQuery } from '../api';
import type { ServiceRequestExportParams } from './types';

export interface ExportServiceRequestsParams {
  onSuccess?: () => void;
  /** Sole error channel — the returned promise always resolves, callers don't need try/catch */
  onError?: (error: unknown) => void;
}

export const useExportServiceRequests = ({ onSuccess, onError }: ExportServiceRequestsParams) => {
  const { projectId, siteId } = useProjectSiteParams();

  const [exportRequests, { isLoading, isFetching, isSuccess, isError, error }] = useLazyExportServiceRequestsQuery();

  const handleExport = useCallback(
    async (params: Omit<ServiceRequestExportParams, 'projectId' | 'siteId'> = {}) => {
      try {
        await exportRequests({
          ...params,
          projectId,
          siteId
        }).unwrap();

        onSuccess?.();
      } catch (error) {
        onError?.(error);
      }
    },
    [exportRequests, projectId, siteId, onSuccess, onError]
  );

  return {
    exportServiceRequests: handleExport,
    isLoading: isLoading || isFetching,
    isSuccess,
    isError,
    error
  };
};
